import type { StringCell } from "./types";
import { addressFromRC } from "../rules/audit/addressing";
import { getWorkbookModel, ValueKind } from "../model";

/** Sort A1 addresses by (row, col) rather than lexically — A2 < A10 < B1. */
function compareAddressByRowCol(a: string, b: string): number {
  const pa = parseA1(a);
  const pb = parseA1(b);
  return pa.row - pb.row || pa.col - pb.col;
}

function parseA1(addr: string): { row: number; col: number } {
  const m = addr.match(/^([A-Z]+)(\d+)$/);
  if (!m) return { row: 0, col: 0 };
  return { row: parseInt(m[2], 10), col: columnLettersToIndex(m[1]) };
}

function columnLettersToIndex(letters: string): number {
  let n = 0;
  for (const ch of letters) {
    n = n * 26 + (ch.charCodeAt(0) - 64);
  }
  return n;
}

/**
 * Scan every worksheet for cells whose value type is String. Returns
 * one StringCell per cell (no deduplication — clicking a row navigates
 * to a specific location, so we want every occurrence).
 *
 * Reads from the shared WorkbookModel — if audit already built it,
 * this is essentially free. Otherwise it triggers a fresh ingestion
 * via getWorkbookModel.
 *
 * One TEXT cell → one StringCell. No deduplication: each occurrence is
 * a distinct destination for click-to-navigate. (The model interns the
 * value strings for memory; the consumer sees the same value repeated
 * in StringCell.value for every cell that holds it.)
 */
export async function scanStrings(): Promise<StringCell[]> {
  const model = await getWorkbookModel("workbook");
  const out: StringCell[] = [];
  const textValueStrings = model.catalogs.textValueStrings;

  for (const sheet of Array.from(model.sheets.values())) {
    const { rows, cols, startRow, startCol } = sheet.bounds;
    const cellValueKind = sheet.cellValueKind;
    const textValueIds = sheet.textValueIds;

    for (let r = 0; r < rows; r++) {
      const rowBase = r * cols;
      for (let c = 0; c < cols; c++) {
        const idx = rowBase + c;
        if (cellValueKind[idx] !== ValueKind.TEXT) continue;
        const id = textValueIds[idx];
        if (id < 0) continue;
        const value = textValueStrings[id];
        out.push({
          sheetName: sheet.name,
          address: addressFromRC(startRow + r, startCol + c),
          value,
          length: value.length,
        });
      }
    }
  }

  out.sort(
    (a, b) => a.sheetName.localeCompare(b.sheetName) || compareAddressByRowCol(a.address, b.address)
  );
  return out;
}
